import * as d3 from "d3";
import { useEffect, useRef, useMemo } from "react";
import useContainerDimensions from "./useContainerDimensions";

type DataItem = {
  name: string;
  value: number;
};

type LollipopProps = {
  data: DataItem[],
  title: string;
};

const LollipopPlot = ({data, title}: LollipopProps) => {
  const ref = useRef<SVGSVGElement>(null);
  const {ref: containerRef, dimensions} = useContainerDimensions();
  const width = dimensions.width;
  const height = dimensions.height;
  const sortData = useMemo(() => {
    return [...data].sort((a, b) => a.value - b.value);
  }, [data]);
  
 const allGroups = useMemo(() => data.map(group => group.name), [data]);

    //Color de áreas
    const colorScale = useMemo(() => {
      // Escala que mapea el nombre del grupo (dominio) a un color (rango)
      return d3.scaleOrdinal()
      .domain(allGroups) 
     .range(d3.schemeCategory10); // El rango son los 10 colores estándar de D3
    }, [allGroups]);

  useEffect(() => {

    if (!width || !height || width <= 0 || height <= 0 || !sortData || sortData.length === 0) {
      d3.select(ref.current).selectAll('*').remove();
      return;
    }
    
    const maxLabelLength = d3.max(sortData, (d) => d.name.length) || 0;
    // Factor de escala: 7px por carácter, más un pequeño padding.
    const dynamicMarginLeft = Math.max(7 * maxLabelLength + 30, 80); // Mínimo de 80px

    //ACTUALIZAR MÁRGENES Y ÁREA DE DIBUJO
    const margin = { top: 10, right: 30, bottom: 40, left: dynamicMarginLeft },
          width2 = width - margin.left - margin.right,
          height2 = height - margin.top - margin.bottom;
    // Clear previous chart
    const svgElement = d3.select(ref.current)
            .attr("width", width)
            .attr("height", height);

    svgElement.selectAll('*').remove();

    //Creacion de svg
    const svg = svgElement 
    .append("g")
    .attr("transform", `translate(${margin.left-(margin.left*.3)}, ${margin.top})`);

    const xScale = d3
      .scaleLinear()
      .domain([0, d3.max(sortData, (d) => d.value) as number])
      .range([0, width2]);

    const yScale = d3
      .scaleBand()
      .domain(sortData.map((d) => d.name))
      .rangeRound([height2,1])
      .padding(1);

    //Axis x
    svg
      .append("g")
      .attr("transform", `translate(0, ${height2})`)
      .call(d3.axisBottom(xScale))
      .selectAll("text")
      .attr("transform", "translate(0,0)rotate(-45)")
      .style("text-anchor", "end");

    //Eje y  o axis y
    svg
      .append("g")
      .call(d3.axisLeft(yScale));

    //Lineas de data
    svg
      .selectAll("myline")
      .data(sortData)
      .join("line")
      .attr("x1", (d) => xScale(d.value))
      .attr("x2", xScale(0))
      .attr("y1", (d) => yScale(d.name)!)
      .attr("y2", (d) => yScale(d.name)!)
      .attr("stroke", (d) => colorScale(d.name) as string);

    //Circulos del final
    svg
      .selectAll("mycircle")
      .data(sortData)
      .join("circle")
      .attr("cx", (d) => xScale(d.value))
      .attr("cy", (d) => yScale(d.name)!)
      .attr("r", "4")
      .style("fill", (d) => colorScale(d.name) as string)
      .attr("stroke", (d) => colorScale(d.name) as string);

    svg.append("text")
      .attr("x", width2 / 2)
      .attr("y", -margin.top / 2 + 20)
      .attr("text-anchor", "middle")
      .style("font-size", "16px")
      .text(title);
  }, [data, width, height, sortData]);

  return ( 
    <div ref={containerRef} style={{ width: '100%', height: '100%', minHeight: '300px' }}>
      <svg ref={ref} width={width} height={height} className="responsive-svg" />
    </div>
  )
};

export default LollipopPlot;
